import type {
  FilterDefinition,
  Filter,
  FilterMatchMode,
} from "@/shared/components/ResourceView";
import { matchesStringListFilter } from "@/shared/domain/query/matchesListFilter";
import type { UserGroupRecord } from "@/shared/domain/userGroups";

/** The fields the user groups filter widget offers. */
export const USER_GROUP_FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    type: "string",
    field: "name",
    name: "Name",
    allowedComparators: ["CONTAINS", "EQUALS"],
  },
  {
    type: "string",
    field: "members",
    name: "Member",
    allowedComparators: ["CONTAINS", "EQUALS", "NOT_CONTAINS"],
  },
  {
    type: "string",
    field: "created_by",
    name: "Created by",
    allowedComparators: ["CONTAINS", "EQUALS"],
  },
];

/**
 * Case-insensitive match of one value against the positive form of a
 * comparator: `NOT_CONTAINS` is checked as `CONTAINS`, and the caller negates.
 */
function positiveMatch(
  haystack: string,
  comparator: string,
  needle: string,
): boolean {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  switch (comparator.replace(/^NOT_/, "")) {
    case "EQUALS":
      return h === n;
    case "CONTAINS":
    case "LIKE":
      return h.includes(n);
    default:
      // Unknown comparator: don't drop the row over a filter we can't read.
      return true;
  }
}

/**
 * Whether any of `haystacks` matches the filter. Positive comparators match
 * when some haystack matches some value; negative ones match when no
 * haystack matches any value. A group's member list is several haystacks; a
 * scalar field is one, and a field with no non-blank haystack is unset (see
 * `matchesStringListFilter` for how the list comparators treat it).
 *
 * `EQUALS` / `NOT_EQUALS` read only the first value: they take exactly one,
 * and several values of equality are `ANY_OF` / `NONE_OF`.
 */
function matchesStringFilter(
  haystacks: readonly string[],
  comparator: string,
  values: readonly string[],
  includeNull = false,
): boolean {
  if (comparator === "ANY_OF" || comparator === "NONE_OF") {
    return matchesStringListFilter(haystacks, comparator, values, includeNull);
  }
  const read =
    comparator === "EQUALS" || comparator === "NOT_EQUALS"
      ? values.slice(0, 1)
      : values;
  const needles = read.filter((v) => v.trim().length > 0);
  if (needles.length === 0) {
    return true;
  }
  const anyMatch = haystacks.some((h) =>
    needles.some((n) => positiveMatch(h, comparator, n)),
  );
  return comparator.startsWith("NOT_") ? !anyMatch : anyMatch;
}

function matchesFilter(group: UserGroupRecord, filter: Filter): boolean {
  if (filter.type !== "string") {
    return true;
  }
  switch (filter.field) {
    case "name":
      return matchesStringFilter(
        [group.name],
        filter.comparator,
        filter.values,
        filter.includeNull,
      );
    case "members":
      return matchesStringFilter(
        group.members,
        filter.comparator,
        filter.values,
        filter.includeNull,
      );
    case "created_by":
      return matchesStringFilter(
        [group.created_by],
        filter.comparator,
        filter.values,
        filter.includeNull,
      );
    default:
      // A field in the widget but unhandled here would silently match every
      // row; keep this in sync with USER_GROUP_FILTER_DEFINITIONS.
      return true;
  }
}

/**
 * In-memory evaluation of the filter widget's output against one user group.
 * The groups endpoint returns every group in one response and has no search,
 * so filters are applied here rather than sent to the server.
 *
 * `matchMode` joins distinct filters: `"AND"` requires every filter to match,
 * `"OR"` at least one. An empty filter list matches every group.
 */
export function userGroupMatchesFilters(
  group: UserGroupRecord,
  filters: readonly Filter[],
  matchMode: FilterMatchMode,
): boolean {
  if (filters.length === 0) {
    return true;
  }
  return matchMode === "OR"
    ? filters.some((f) => matchesFilter(group, f))
    : filters.every((f) => matchesFilter(group, f));
}
